import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AppShell from "@/components/platform/AppShell";
import { PageTitle } from "@/components/platform/Badges";
import { VendorDashboard } from "@/components/vendors/VendorDashboard";
import { ALL, InventoryFilters, VendorInventory, VendorPagination } from "@/components/vendors/VendorInventory";
import { NewVendorDialog } from "@/components/vendors/NewVendorDialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useVendorMeta } from "@/lib/vendors";

const BLANK = { q: "", risk: ALL, contract_status: ALL, service_type: ALL, hosting: ALL, assessment: ALL, status: ALL };
const PAGE_SIZE = 50;

export default function VendorRegister() {
  const auth = useAuth();
  const meta = useVendorMeta();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") || "dashboard";
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState(BLANK);
  const [adding, setAdding] = useState(false);

  useEffect(() => { api.get("/vendors/dashboard").then(({ data }) => setDash(data)).catch(() => {}); }, []);
  useEffect(() => { setPage(1); }, [f]);
  const load = useCallback(async () => {
    const p = { q: f.q || undefined, page, limit: PAGE_SIZE };
    Object.entries(f).forEach(([k, v]) => { if (k !== "q" && v !== ALL) p[k] = v; });
    try {
      const res = await api.get("/vendors", { params: p });
      setRows(res.data.vendors);
      setTotal(parseInt(res.headers["x-total-count"], 10) || res.data.count);
    } finally { setLoading(false); }
  }, [f, page]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  return (
    <AppShell testId="vendor-register-page">
      <PageTitle eyebrow="Operations workspace" title="Vendor Register"
                 subtitle="Third parties that process personal data, linked to the RoPA, with inherent risk, assessment score, residual risk and contract controls."
                 actions={auth.can("vendors", "create") && (
                   <Button onClick={() => setAdding(true)} className="bg-[#008080] hover:bg-[#008080]/90" data-testid="vendor-new-button">
                     <Plus className="w-4 h-4 mr-1" /> Add vendor
                   </Button>
                 )} />
      <Tabs value={view} onValueChange={(v) => setParams({ view: v })}>
        <TabsList className="bg-white border border-slate-200" data-testid="vendor-view-tabs">
          <TabsTrigger value="dashboard" data-testid="vendor-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="vendor-tab-inventory">Inventory ({total})</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-5"><VendorDashboard d={dash} /></TabsContent>
        <TabsContent value="inventory" className="mt-5 space-y-4">
          {meta && <InventoryFilters f={f} set={set} meta={meta} />}
          <VendorInventory rows={rows} loading={loading} />
          <VendorPagination page={page} limit={PAGE_SIZE} total={total} onPage={setPage} />
        </TabsContent>
      </Tabs>
      <NewVendorDialog open={adding} onOpenChange={setAdding} meta={meta} />
    </AppShell>
  );
}
